import { Button } from '@neo/primitives/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import { Input } from '@neo/primitives/input'
import { Textarea } from '@neo/primitives/textarea'
import { useState } from 'react'
import { useEditVersionSummary } from '../../queries'
import type { FlowVersion } from '../../types'

// Keep in sync with VERSION_SUMMARY_MAX_CHARS / VERSION_DESCRIPTION_MAX_CHARS in
// config-service functions.py — the backend trims to the same caps, this just stops
// over-typing.
const TITLE_MAX_CHARS = 100
const DESCRIPTION_MAX_CHARS = 300

// Manually edit a version's git-style change message — a short title plus a longer
// description. The AI phrasing can be wrong. A dialog keeps the row a pure display element
// and avoids nesting inputs inside the row's click-to-view button. Mirrors
// useMakeVersionActive so the mutation + dialog live in one place. (SER-5645)
export const useEditVersionDescription = (phloID: string | undefined) => {
  const { mutate, isPending } = useEditVersionSummary()
  const [target, setTarget] = useState<FlowVersion | null>(null)
  const [titleDraft, setTitleDraft] = useState('')
  const [descDraft, setDescDraft] = useState('')
  // Untouched description is OMITTED from the save so the backend leaves the stored
  // value as-is. The drafts snapshot at dialog-open; without this, a title-only edit
  // on a just-saved version would post the stale (empty) description and permanently
  // null an AI description that landed via the poll while the dialog was open.
  const [descTouched, setDescTouched] = useState(false)

  const requestEdit = (version: FlowVersion) => {
    setTitleDraft(version.change_summary ?? '')
    setDescDraft(version.change_description ?? '')
    setDescTouched(false)
    setTarget(version)
  }

  const close = () => {
    setTarget(null)
    setTitleDraft('')
    setDescDraft('')
    setDescTouched(false)
  }

  const title = titleDraft.trim()
  const description = descDraft.trim()
  // Title is required; save only when something actually changed.
  const changed =
    title !== (target?.change_summary ?? '') ||
    (descTouched && description !== (target?.change_description ?? ''))
  const canSave = !!title && changed && !isPending

  const save = () => {
    if (!phloID || !target || !canSave) return
    mutate(
      {
        phloID,
        versionId: target.id,
        summary: title,
        ...(descTouched && { description }),
      },
      { onSuccess: close },
    )
  }

  const dialog = (
    <Dialog
      open={!!target}
      onOpenChange={(next) => {
        if (!next && !isPending) close()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit description</DialogTitle>
          <DialogDescription>
            A short title and a plain-English description of what changed in this version.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-s-500 text-primary">Title</span>
            <Input
              autoFocus
              value={titleDraft}
              maxLength={TITLE_MAX_CHARS}
              placeholder="e.g. Add SMS confirmation step"
              onChange={(e) => setTitleDraft(e.target.value)}
              data-testid="input-version-summary"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-s-500 text-primary">Description</span>
            <Textarea
              value={descDraft}
              maxLength={DESCRIPTION_MAX_CHARS}
              rows={3}
              placeholder="What changed and why it matters, in plain language."
              onChange={(e) => {
                setDescDraft(e.target.value)
                setDescTouched(true)
              }}
              data-testid="input-version-description"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={close}
            disabled={isPending}
            data-testid="button-version-summary-cancel"
          >
            Cancel
          </Button>
          <Button onClick={save} disabled={!canSave} data-testid="button-version-summary-save">
            {isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  return { requestEdit, dialog, isPending }
}
